import { useCallback, useEffect, useState } from 'react'
import { SearchConfigProps, SearchStateProps } from 'components/WorkspaceDashboardSearchBar/Types'
import { AlmanacFile, Auth, SetState } from 'types'
import { useAuth } from 'hooks/UseAuth'
import produce from 'immer'

interface Props {
  setSearchConfiguration: SetState<SearchConfigProps>
  setSearchState: SetState<SearchStateProps>
  workspaceId: number
}

export const getIndexFilters = ({
  workspaceId,
  auth,
  folderId,
  fileType,
}: {
  workspaceId: number
  auth: Auth | null
  folderId?: number | null
  fileType?: string | null
}) => {
  if (!auth) return ''

  let query = `viewable_by:${auth?.id}`
  if (fileType && ['article', 'project'].includes(fileType)) query = `${query} AND file_type:${fileType}`
  if (folderId) query = `${query} AND ancestry:${folderId}`
  if (!query.includes('company')) query = `${query} AND company:${workspaceId}`

  return query
}

export const useSearch = ({ setSearchConfiguration, setSearchState, workspaceId }: Props) => {
  const auth = useAuth()
  const [filterFolder, setFilterFolder] = useState<AlmanacFile | undefined | null>()
  const [filterFileType, setFilterFileType] = useState<string | undefined>()

  const clearRefinements = useCallback(() => {
    setSearchState((oldState) => ({
      ...oldState,
      refinementList: undefined,
      multiRange: undefined,
    }))
  }, [setSearchState])

  useEffect(() => {
    return () => {
      clearRefinements()
    }
  }, [clearRefinements])

  const resetSearchPage = useCallback(() => {
    setSearchState(
      produce((oldState) => {
        oldState.page = 1
        if (oldState.indices) {
          const { articles_index } = oldState.indices
          if (articles_index) articles_index.page = 1
        }
      }),
    )
  }, [setSearchState])

  const setFolderFilter = useCallback(
    (folder: AlmanacFile | undefined | null) => {
      setFilterFolder(folder)

      resetSearchPage()
      setSearchConfiguration((oldConfig) => {
        return {
          ...oldConfig,
          filters: getIndexFilters({ workspaceId, auth, folderId: folder?.project_id, fileType: filterFileType }),
        }
      })
    },
    [resetSearchPage, setSearchConfiguration, workspaceId, auth, filterFileType],
  )

  const setFileTypeFilter = useCallback(
    (fileType: string | undefined) => {
      setFilterFileType(fileType)

      resetSearchPage()
      setSearchConfiguration((oldConfig) => {
        return {
          ...oldConfig,
          filters: getIndexFilters({ workspaceId, auth, fileType, folderId: filterFolder?.project_id }),
        }
      })
    },
    [resetSearchPage, setSearchConfiguration, workspaceId, auth, filterFolder],
  )

  return {
    filterFileType,
    filterFolder,
    setFileTypeFilter,
    setFolderFilter,
  }
}
